import React, { useEffect, useState } from 'react';
import Header from './components/header/header.jsx';
import Sidebar from './components/sidebar/sidebar.jsx';
import Content from './components/content/content.jsx';
import Footer from './components/footer/footer.jsx';
import { AppSettings } from './config/app-settings.js';
import useAuth from './hooks/useAuth';
import { useLocation } from 'react-router-dom';
import ThemePanel from './components/theme-panel/theme-panel.jsx';
import ToastContainer from './components/ui/toastContainer.jsx';
import toastService from './services/toastService.js';

function App() {
	const [appMode, setAppMode] = useState('light');
	const [appTheme, setAppTheme] = useState('theme-default');
	const [appCover, setAppCover] = useState('');
	const [appHeaderNone, setAppHeaderNone] = useState(false);
	const [appSidebarNone, setAppSidebarNone] = useState(false);
	const [appContentClass, setAppContentClass] = useState('');
	const [appSidebarEnd, setAppSidebarEnd] = useState(false);
	const [appSidebarWide, setAppSidebarWide] = useState(false);
	const [appSidebarLight, setAppSidebarLight] = useState(false);
	const [appSidebarMinify, setAppSidebarMinify] = useState(false);
	const [appSidebarMobileToggled, setAppSidebarMobileToggled] = useState(false);
	const [appTopNav, setAppTopNav] = useState(false);
	const [appBoxedLayout, setAppBoxedLayout] = useState(false);
	const [appFooter, setAppFooter] = useState(true);

	const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isLoginRoute = location.pathname.startsWith('/pages/login');
  const showChrome = !isLoginRoute && isAuthenticated;

  // Quota alert: show toast when usage crosses 80% or at 100%
  useEffect(() => {
    let cancelled = false;
    async function checkQuotaOnce() {
      try {
        if (!showChrome) return;
        // use sessionStorage flags to avoid repeat toasts per tab session
        const alreadyNotified80 = sessionStorage.getItem('quotaWarn80');
        const alreadyNotified100 = sessionStorage.getItem('quotaWarn100');
        const { default: authService } = await import('./services/authService');
        const data = await authService.getOrgQuota();
        if (!data || data.ok === false) return;
        const pct = Number(data.usage_percent || 0);
        const orgName = data.organisation || 'your organization';
        if (!cancelled && pct >= 100 && !alreadyNotified100) {
          toastService.error(`Quota exhausted for ${orgName}: ${pct}% used. New jobs may be blocked until next cycle.`, 15000);
          sessionStorage.setItem('quotaWarn100', '1');
        } else if (!cancelled && pct >= 80 && !alreadyNotified80) {
          toastService.warning(`High usage for ${orgName}: ${pct}% of monthly tokens used.`, 10000);
          sessionStorage.setItem('quotaWarn80', '1');
        }
      } catch (e) {
        // silent fail
      }
    }
    checkQuotaOnce();
    return () => { cancelled = true; };
  }, [showChrome]);

	var handleSetAppTheme = (value) => {
		if (value) {
			var newTheme = value;
			for (var x = 0; x < document.body.classList.length; x++) {
				if ((document.body.classList[x]).indexOf('theme-') > -1 && document.body.classList[x] !== newTheme) {
					document.body.classList.remove(document.body.classList[x]);
				}
			}
			
			if (localStorage && value) {
				localStorage.appTheme = value;
			}
			
			document.body.classList.add(newTheme);
			document.dispatchEvent(new Event('theme-reload'));
		}
	}
	
	var handleSetAppMode = (value) => {
		if (value) {
			document.documentElement.setAttribute('data-bs-theme', value);
		}
	}
		
	var handleSetAppCover = (value) => {
		if (value) {
			var htmlElm = document.querySelector('html');
			for (var x = 0; x < document.documentElement.classList.length; x++) {
				var targetClass = document.documentElement.classList[x];
				if (targetClass.search('bg-cover-') > -1) {
					htmlElm.classList.remove(targetClass);
				}
			}
			htmlElm.classList.add(value);
		
			if (localStorage && value) {
				localStorage.appCover = value;
			}
		}
	}
	
	useEffect(() => {
		// Load persisted preferences if present
		if (localStorage) {
			if (localStorage.appTheme) {
				handleSetAppTheme(localStorage.appTheme);
			}
			if (localStorage.appMode) {
				handleSetAppMode(localStorage.appMode);
			}
			if (localStorage.appCover) {
				handleSetAppCover(localStorage.appCover);
			}
		}
		// eslint-disable-next-line
	}, []);
	
  // Compute layout helper classes
  const withoutHeader = !showChrome || appHeaderNone;
  const withoutSidebar = !showChrome || appSidebarNone;
  const appRootClassNames =
    'app ' +
    (appTopNav ? 'app-with-top-nav ' : '') +
    (appBoxedLayout ? 'app-boxed-layout ' : '') +
    (withoutHeader ? 'app-without-header ' : '') +
    (withoutSidebar ? 'app-without-sidebar ' : '');
	
	return (
		<AppSettings.Provider value={{
			appMode, setAppMode,
			appTheme, setAppTheme,
			appCover, setAppCover,
			appHeaderNone, setAppHeaderNone,
			appSidebarNone, setAppSidebarNone,
			appContentClass, setAppContentClass,
			appSidebarEnd, setAppSidebarEnd,
			appSidebarWide, setAppSidebarWide,
			appSidebarLight, setAppSidebarLight,
			appSidebarMinify, setAppSidebarMinify,
			appSidebarMobileToggled, setAppSidebarMobileToggled,
			appTopNav, setAppTopNav,
			appBoxedLayout, setAppBoxedLayout,
			appFooter, setAppFooter
		}}>
			<div className={appRootClassNames}>
          {showChrome && <Header />}
          {showChrome && <Sidebar />}
          <Content className={appContentClass} />
          {showChrome && <Footer />}
          {showChrome && <ThemePanel />}
          <ToastContainer />
			</div>
		</AppSettings.Provider>
	)
}

export default App;